/**
 * Sécurité transport auth (SSL/TLS).
 * - Prod / hôtes distants : forcer HTTPS
 * - Localhost : HTTP autorisé (dev)
 * - Ne jamais attacher un Bearer sur HTTP distant
 */

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

export function isLocalDevHost(hostname: string): boolean {
  const h = String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  return LOCAL_HOSTS.has(h) || h.endsWith('.localhost');
}

/** True si l’URL est assez sûre pour y coller un JWT (HTTPS ou localhost). */
export function isSafeToAttachAuthToken(url: string): boolean {
  const raw = String(url || '').trim();
  if (!raw) {
    return false;
  }
  try {
    const absolute = /^https?:\/\//i.test(raw)
      ? raw
      : `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost'}${
          raw.startsWith('/') ? raw : `/${raw}`
        }`;
    const u = new URL(absolute);
    if (u.protocol === 'https:') {
      return true;
    }
    if (u.protocol === 'http:' && isLocalDevHost(u.hostname)) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Remplace http:// par https:// pour les hôtes non locaux.
 * Ne touche pas aux chemins relatifs ni au localhost.
 */
export function ensureHttpsUrl(url: string): string {
  const raw = String(url || '').trim();
  if (!raw) {
    return raw;
  }
  try {
    if (!/^https?:\/\//i.test(raw)) {
      return raw;
    }
    const u = new URL(raw);
    if (u.protocol === 'http:' && !isLocalDevHost(u.hostname)) {
      u.protocol = 'https:';
      return u.toString();
    }
    return raw;
  } catch {
    return raw;
  }
}

/** Page courante : HTTPS ou localhost (autorise le transfert `auth=`). */
export function isSecureAuthPageContext(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  const { protocol, hostname } = window.location;
  if (protocol === 'https:') {
    return true;
  }
  return protocol === 'http:' && isLocalDevHost(hostname);
}

/**
 * Bloque / logue le transfert cross-front de tokens si la page n’est pas TLS.
 * Retourne false si le transfert doit être refusé.
 */
export function assertSecureContextForAuthTransfer(contextLabel = 'AuthSync'): boolean {
  if (isSecureAuthPageContext()) {
    return true;
  }
  console.error(
    `[${contextLabel}] Refus transfert auth : page non sécurisée (${window.location.origin}). Utilisez HTTPS.`
  );
  return false;
}
